import { Component, EventEmitter, inject, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { AngularSvgIconModule } from "angular-svg-icon";
import { DrawerModule } from "primeng/drawer";
import { LoggerService } from "../../../ancestors/logger.service";
import { TranslatePipe } from "../../pipe/translation.filter.pipe";
import { ApeHeaderMenuItemComponent, MenuItem } from "../ape-header-menu-item/ape-header-menu-item.component";
import { ApeAvatarComponent } from "../ape-avatar/ape-avatar.component";
import { ApeModalComponent } from "../ape-modal/ape-modal.component";
import { UrlCollection } from "../../constants/url-collection";
import { LogoutService } from "../../services/logout.service";
import { UserDataService } from "../../services/data/user-data.service";
import { TRANSLATION_KEYS } from "../../constants/translations/translation-keys";
import { AutoFocusModule } from "primeng/autofocus";
import { FocusTrapModule } from "primeng/focustrap";
import { ApeButtonComponent } from "../ape-button/ape-button.component";

/**
 * Componente per il menu dell'header
 * Componente completamente indipendente e immutabile che gestisce navigazione e menu
 *
 * Caratteristiche:
 * - Drawer responsive per tutti i dispositivi
 * - Navigazione autonoma tramite Router
 * - Voci di menu predefinite fisse
 * - Gestione eventi di apertura/chiusura completamente interna
 * - Supporto completo per accessibilità
 * - Proprietario dei link e della logica di navigazione
 * - Configurazione immutabile (nessun input/output esterno)
 */
@Component({
  selector: "ape-header-menu",
  standalone: true,
  imports: [
    CommonModule,
    AngularSvgIconModule,
    DrawerModule,
    TranslatePipe,
    ApeHeaderMenuItemComponent,
    ApeAvatarComponent,
    ApeModalComponent,
    AutoFocusModule,
    FocusTrapModule,
    ApeButtonComponent
  ],
  templateUrl: "./ape-header-menu.component.html"
})
export class ApeHeaderMenuComponent {
  protected readonly TRANSLATION_KEYS = TRANSLATION_KEYS;

  /**
   * Evento emesso quando viene cliccata la voce Supporto nel menu
   */
  @Output() openSupportSidenav = new EventEmitter<void>();

  private router = inject(Router);

  private logger = inject(LoggerService);

  private logoutService = inject(LogoutService);

  private userDataService = inject(UserDataService);

  constructor() {
    this.logger.setCaller(this.constructor.name);
  }

  /**
   * Determina se il drawer è visibile (gestito internamente)
   */
  protected visible = false;

  /**
   * Determina se la modale di conferma logout è visibile
   */
  protected logoutModalVisible = false;

  /**
   * Nome dell'utente per l'avatar (dal UserDataService)
   */
  protected get userFirstName(): string {
    const loginInfo = this.userDataService.getJwtPayload();
    return loginInfo?.user?.forename || "";
  }

  /**
   * Cognome dell'utente per l'avatar (dal UserDataService)
   */
  protected get userLastName(): string {
    const loginInfo = this.userDataService.getJwtPayload();
    return loginInfo?.user?.surname || "";
  }

  /**
   * Avatar dell'utente recuperato dal JWT payload
   */
  protected get userAvatar(): string {
    const loginInfo = this.userDataService.getJwtPayload();
    return loginInfo?.user?.avatarImage || "";
  }

  /**
   * Configurazione delle voci del menu (filtrata in base ai permessi)
   * Il componente è proprietario di questa configurazione
   */
  protected get menuItems(): MenuItem[] {
    const allMenuItems: MenuItem[] = [
      {
        label: "menu.SUPPORT",
        icon: "assets/icons/circle-question-mark.svg",
        iconType: "svg",
        command: () => {
          this.onSupportClick();
        }
      },
      {
        label: "menu.ACCOUNT_EMPLOYEE",
        icon: "assets/icons/user.svg",
        iconType: "svg",
        command: () => {
          this.onAccountEmployeeClick();
        }
      },
      {
        label: "menu.ACCOUNT_ADMIN",
        icon: "assets/icons/user-cog.svg",
        iconType: "svg",
        command: () => {
          this.onAccountAdminClick();
        },
        // Nascondi se l'utente non è admin o superadmin
        hidden: !this.userDataService.isAdmin() && !this.userDataService.isSuperAdmin()
      },
      {
        label: "menu.PERSONAL_PROFILE",
        icon: "",
        iconType: "avatar",
        firstName: this.userFirstName,
        lastName: this.userLastName,
        image: this.userAvatar,
        command: () => {
          this.onPersonalProfileClick();
        }
      },
      {
        label: "menu.LOGOUT",
        icon: "assets/icons/log-out.svg",
        iconType: "svg",
        command: () => {
          this.onLogoutClick();
        }
      }
    ];

    // Filtra le voci nascoste
    return allMenuItems.filter(item => !item.hidden);
  }

  /**
   * Chiude il drawer
   */
  protected closeDrawer(): void {
    this.visible = false;
  }

  /**
   * Gestisce il click su Supporto
   */
  private onSupportClick(): void {
    this.closeDrawer();
    this.openSupportSidenav.emit();
  }

  /**
   * Gestisce il click su Account Employee
   */
  private onAccountEmployeeClick(): void {
    this.closeDrawer();
    void this.router.navigate([UrlCollection.homepage]);
  }

  /**
   * Gestisce il click su Account Admin
   */
  private onAccountAdminClick(): void {
    this.closeDrawer();
    void this.router.navigate([UrlCollection.adminHomepage]);
  }

  /**
   * Gestisce il click su Profilo personale
   */
  private onPersonalProfileClick(): void {
    this.closeDrawer();
    void this.router.navigate([UrlCollection.profile]);
  }

  /**
   * Gestisce il click su Logout
   * Chiude il drawer e mostra la modale di conferma
   */
  private onLogoutClick(): void {
    this.logger.info("User clicked logout from header menu");
    this.closeDrawer();
    this.logoutModalVisible = true;
  }

  /**
   * Gestisce la conferma del logout
   * Esegue la procedura di logout completa
   */
  protected async onConfirmLogout(): Promise<void> {
    this.logger.info("User confirmed logout");
    this.logoutModalVisible = false;

    try {
      await this.logoutService.performLogout();
    } catch (error) {
      this.logger.error("Error during logout from header menu", String(error));
      // Il LogoutService già gestisce gli errori internamente
    }
  }

  /**
   * Gestisce l'annullamento del logout
   * Chiude la modale senza eseguire il logout
   */
  protected onCancelLogout(): void {
    this.logger.info("User cancelled logout");
    this.logoutModalVisible = false;
  }

  /**
   * Funzione trackBy per le voci del menu
   * @param index - Indice dell'elemento
   * @param item - Voce del menu
   * @returns Etichetta della voce come identificatore univoco
   */
  trackByMenuItemLabel(index: number, item: MenuItem): string {
    return item.label;
  }
}
